import * as THREE from "three";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import Camera from "./utils/Camera.js";
import Utils from "./utils/Utils.js";
import Raycaster from "./utils/Raycaster.js";
// import ModelLoader from "./utils/ModelLoader.js";
import ModelManager from "./utils/ModelManager.js";
import ModelConfig from "./static/json/models.json";
import TextLabel from "./utils/TextLabel.js";


export default class App {
  constructor() {
    this.container = document.body;
    this.assetColor = 0xFFFBF2;
    this.modelSize = 0.2;
    this.backgroundColor = 0xeff1f2;
    this.model = null;
    this.model2 = null;
    this.textLabels = [];
    this.modelsConfig = null;
    this.init();
  }

  async init() {
    this.setupRenderer();
    this.setupScene();
    this.setupCamera();
    
    // Optional: Load models configuration (for future use)
    // await this.loadModelsConfig();
    
    await this.loadEnvironment();
    await this.loadModels();
    
    this.setupTextLabels();
    this.setupButtons();
    this.startAnimation();
  }

  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.setClearColor(this.backgroundColor);
    this.container.appendChild(this.renderer.domElement);
  }

  setupScene() {
    this.scene = new THREE.Scene();
  }

  setupCamera() {
    // Create camera instance with custom settings
    this.cameraInstance = new Camera(this.renderer, {
      fov: 45,
      aspect: window.innerWidth / window.innerHeight,
      near: 0.1,
      far: 1000,
      position: { x: 0, y: 2, z: 5 },
      enableDamping: true,
      dampingFactor: 0.02,
      screenSpacePanning: false,
      minDistance: 10,
      maxDistance: 17,
      enablePan: false,
      enableZoom: true
    });
    
    // Get camera and controls references for use in App
    this.camera = this.cameraInstance.getCamera();
    this.controls = this.cameraInstance.getControls();
  }

  // async loadModelsConfig() {
  //   try {
  //     const configURL = new URL("../models.json", import.meta.url);
  //     this.modelsConfig = await Utils.loadJSON(configURL.href);
  //     console.log("Models configuration loaded:", this.modelsConfig);
  //   } catch (error) {
  //     console.error("Failed to load models configuration:", error);
  //     // Don't throw error - just continue with hardcoded values
  //   }
  // }


  // __________________________________________ ENVIRONMENT LOADER __________________________________________

  async loadEnvironment() {
    const hdrTextureURL = new URL("../static/img/photostudio.hdr", import.meta.url);
    const hdrLoader = new RGBELoader();
    
    return new Promise((resolve) => {
      hdrLoader.load(hdrTextureURL.href, (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        this.scene.environment = texture;
        resolve();
      });
    });
  }


  // __________________________________________ MODELS LOADER HARDCODED __________________________________________

  async loadModels() {
    const manifoldFullURL = new URL("../static/3d/manifold_full.glb", import.meta.url);
    const manifoldSlicedURL = new URL("../static/3d/manifold_sliced.glb", import.meta.url);
    const roughnessTextureURL = new URL("../static/img/roughness/roughness2.jpg", import.meta.url);
    
    const loader = new GLTFLoader();
    const textureLoader = new THREE.TextureLoader();
    const roughnessTexture = textureLoader.load(roughnessTextureURL.href);

    // Load first model (manifold full)
    await new Promise((resolve) => {
      loader.load(manifoldFullURL.href, (gltf) => {
        this.model = gltf.scene;
        this.setupModel(this.model, true);
        this.scene.add(this.model);
        resolve();
      });
    });

    // Load second model (manifold sliced)
    await new Promise((resolve) => {
      loader.load(manifoldSlicedURL.href, (gltf) => {
        this.model2 = gltf.scene;
        this.setupModel(this.model2, false);
        this.scene.add(this.model2);
        resolve();
      });
    });
  }

  setupModel(model, visible = true) {
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: this.assetColor,
          roughness: 0.6,
          metalness: 0.5,
          envMapIntensity: 1.5
        });
      }
    });

    model.position.set(0, 0, 0);
    model.rotation.set(0, 0, 0);
    model.scale.set(this.modelSize, this.modelSize, this.modelSize);
    model.visible = visible;
  }



  // __________________________________________ TEXTS LABELS __________________________________________

  setupTextLabels() {
    // Define your labels with content, position, and optional CSS class
    const labelsConfig = [
      {
        text: "Poids: 25g",
        position: { x: -0.3, y: 0.3, z: 0.2 },
        className: "text-label info"
      },
      {
        text: "Largeur: 110mm",
        position: { x: -5, y: -0.1, z: 0.1 },
        className: "text-label info"
      },
      {
        text: "Hauteur: 10mm",
        position: { x: 4, y: -0.5, z: -0.2 },
        className: "text-label warning"
      },
      {
        text: "Matériau: Céramique",
        position: { x: 0.8, y: 0.8, z: 0.5 },
        className: "text-label success"
      },
      {
        text: "Température max: 1200°C",
        position: { x: -3.0, y: -0.3, z: 0.8 },
        className: "text-label error"
      }
    ];

    // Create labels from configuration
    this.textLabels = labelsConfig.map(config => {
      const label = new TextLabel(config.text, config.position, config.className);
      return label;
    });

    console.log("Text labels created:", this.textLabels.length);
  }
  
  // __________________________________________ BUTTONS __________________________________________

  setupButtons() {
    document.getElementById("showFullButton").addEventListener("click", () => {
      this.toggleModels("full");
    });

    document.getElementById("showSlicedButton").addEventListener("click", () => {
      this.toggleModels("sliced");
    });

    // Add zoom button functionality
    document.getElementById("ZoomButton").addEventListener("click", (e) => {
      e.preventDefault(); // Prevent link default behavior
      this.handleZoom();
    });
  }

  toggleModels(activeModel) {
    if (activeModel === "full") {
      if (this.model) this.model.visible = true;
      if (this.model2) this.model2.visible = false;
    } else if (activeModel === "sliced") {
      if (this.model) this.model.visible = false;
      if (this.model2) this.model2.visible = true;
    }
  }

  handleZoom() {
    // Use the Camera class's toggleZoom method
    // closeDistance: how close to zoom in
    // normalDistance: normal viewing distance
    // duration: animation duration in milliseconds
    this.cameraInstance.toggleZoom(4, 20, 3000); // close distance 3, normal distance 8, duration 3000ms
    
    console.log("Zoom toggled");
  }

  // __________________________________________ ANIMATION ROTATION __________________________________________

  animate() {
    // Rotate models
    if (this.model) {
      this.model.rotation.y += 0.0001;
    }
    if (this.model2) {
      this.model2.rotation.y += 0.0001;
    }

    // Update text labels positions
    this.textLabels.forEach(label => {
      label.updateScreenPosition(this.camera, this.renderer);
    });

    this.renderer.render(this.scene, this.camera);
  }

  startAnimation() {
    this.renderer.setAnimationLoop(() => this.animate());
  }
}
